import {
	CartAdjustment as ApiCartAdjustment,
	PaymentType as ApiPaymentType,
	ShippingMethod as ApiShippingMethod,
	GetMerchantDetailsResponse,
	MerchantCharac,
} from '../api/responses/get-merchant-details';
import { CartAdjustment, CreditCard, Merchant, PaymentType, ShippingMethod } from './merchant-details.model';
import {
	filterForApplePayCards,
	filterForCreditCard,
	filterForGooglePayCards,
	filterNonCreditCardPaymentTypes,
} from 'src/app/shared/utilities/credit-cards.utils';
import { validArray, validString } from 'src/app/shared/utilities/types.utils';

import { Dictionary } from 'src/app/shared/types/dictionary';
import { isObject } from 'lodash-es';
import { toInt } from 'src/app/shared/utilities/number.utils';

export function buildMerchantModel(response: GetMerchantDetailsResponse): Merchant {
	const merchant: Merchant = {
		applePayCards: [],
		cartAdjustments: [],
		creditCards: [],
		characs: {},
		googlePayCards: [],
		isTradeMerchant: false,
		isTradesAffiliate: false,
		masterMerchantId: '',
		merchantId: '',
		paymentTypes: [],
		shippingMethods: [],
	};

	if (!isObject(response)) {
		return merchant;
	}

	merchant.masterMerchantId = response.master_merchant_id;
	merchant.merchantId = response.merchant_id;
	merchant.isTradeMerchant = response.is_trade_merchant === '1';
	merchant.isTradesAffiliate = response.trades_affiliate_flag === '1';
	merchant.creditCards = buildCreditCards(response.PAYMENTTYPE, false, false);
	merchant.applePayCards = buildCreditCards(response.PAYMENTTYPE, true, false);
	merchant.googlePayCards = buildCreditCards(response.PAYMENTTYPE, false, true);
	merchant.paymentTypes = buildPaymentTypes(response.PAYMENTTYPE);
	merchant.characs = buildCharacs(response.CHARAC);
	merchant.cartAdjustments = buildCartAdjustments(response.CART_ADJUSTMENTS);
	merchant.shippingMethods = buildShippingMethods(response.SHIPPINGMETHOD);

	return merchant;
}

export function sortByOrder(a: CreditCard | PaymentType, b: CreditCard | PaymentType) {
	return a.order - b.order;
}

export function buildCard(type: ApiPaymentType): CreditCard {
	return {
		authType: type.auth_type,
		billingProviderDesc: type.billing_provider_desc,
		billingProviderId: type.billing_provider_id,
		billingType: type.billing_type,
		cardTypeCode: type.card_type_code,
		cepayMerchantId: type.cepay_merchant_id,
		creditCardTypeId: type.credit_card_type_id,
		enabled3DS: type.enabled_3ds === '1',
		imageFileName: type.image_filename || '',
		order: toInt(type.display_order),
		merchantPaymentTypeRefId: type.merchant_payment_type_ref_id,
		name: type.cct_description,
		paymentTypeId: type.payment_type_id,
		rawCardTypeList: validString(type.raw_card_type_list) ? type.raw_card_type_list.split(',') : [],
		visible: type.visible === 'true',
	};
}

export function buildCreditCards(
	apiPaymentTypes?: ApiPaymentType[],
	isApplePay = false,
	isGooglePay = false
): CreditCard[] {
	if (validArray(apiPaymentTypes)) {
		let cardFilter = filterForCreditCard;

		if (isApplePay) {
			cardFilter = filterForApplePayCards;
		} else if (isGooglePay) {
			cardFilter = filterForGooglePayCards;
		}

		return apiPaymentTypes.filter(cardFilter).map(buildCard).sort(sortByOrder);
	}

	return [];
}

export function buildPaymentType(type: ApiPaymentType): PaymentType {
	return {
		authType: type.auth_type,
		billingId: type.billing_id,
		billingProviderDesc: type.billing_provider_desc,
		billingProviderId: type.billing_provider_id,
		billingType: type.billing_type,
		cepayMerchantId: type.cepay_merchant_id,
		name: type.pt_description,
		order: toInt(type.display_order),
		enable3DS: type.enabled_3ds === '1',
		merchantPaymentTypeRefId: type.merchant_payment_type_ref_id,
		paymentTypeId: type.payment_type_id,
		visible: type.visible === 'true',
	};
}

export function buildPaymentTypes(apiPaymentTypes?: ApiPaymentType[]): PaymentType[] {
	if (validArray(apiPaymentTypes)) {
		return apiPaymentTypes.filter(filterNonCreditCardPaymentTypes).map(buildPaymentType).sort(sortByOrder);
	}

	return [];
}

export function buildCharacs(apiCharacs?: MerchantCharac[]): Dictionary<string> {
	const characs = {};

	if (validArray(apiCharacs)) {
		apiCharacs.forEach((charac: MerchantCharac) => {
			characs[charac.key] = charac.value;
		});
	}

	return characs;
}

export function buildCartAdjustments(apiCartAdjustments?: ApiCartAdjustment[]): CartAdjustment[] {
	if (validArray(apiCartAdjustments)) {
		return apiCartAdjustments.map((adjustment) => {
			return {
				active: adjustment.active_flag === '1',
				amount: parseFloat(adjustment.amount),
				cardType: adjustment.card_type,
				id: adjustment.id,
				name: adjustment.name,
				isPercent: adjustment.percent_flag === '1',
			};
		});
	}
	return [];
}

export function buildShippingMethods(apiShippingMethods?: ApiShippingMethod[]): ShippingMethod[] {
	if (validArray(apiShippingMethods)) {
		return apiShippingMethods.map((method) => {
			return {
				price: parseFloat(method.price),
				name: method.ship_desc,
				description: method.long_desc,
				shipMethod: method.ship_method,
			};
		});
	}
	return [];
}
